import React, { useState, useEffect } from 'react';
import { X, Network, Activity, BookOpen, Loader2, AlertCircle, Info, BarChart3, Dna } from 'lucide-react';
import InfoTooltip from '../../../../../Chat/visualizations/InfoTooltip';
import { api } from '../../../../../../services/api';
import { useAnalysisContext } from '../../../../context/AnalysisContext';
import NetworkGraph from '../../../../../NetworkGraph';
import RescueScoreBarPlot from '../../../../../visualizations/RescueScoreBarPlot';

/**
 * Target Details Modal
 *
 * Shows comprehensive analysis for a selected lncRNA target:
 * - Expression Statistics (differential expression analysis)
 * - Gene Regulatory Network (GRN)
 * - Knockdown Simulation
 * - Biological Insights
 */

interface TargetDetailsModalProps {
  targetGene: string;
  onClose: () => void;
}

type Tab = 'expression' | 'grn' | 'knockdown' | 'insights' | 'aso';

export const TargetDetailsModal: React.FC<TargetDetailsModalProps> = ({
  targetGene,
  onClose,
}) => {
  const { selectedDisease, selectedCellTypes, selectedDatasetId, analysisResult } = useAnalysisContext();
  const [activeTab, setActiveTab] = useState<Tab>('expression');
  const [isLoading, setIsLoading] = useState<Record<Tab, boolean>>({
    expression: false,
    grn: false,
    knockdown: false,
    insights: false,
    aso: false,
  });
  const [errors, setErrors] = useState<Record<Tab, string | null>>({
    expression: null,
    grn: null,
    knockdown: null,
    insights: null,
    aso: null,
  });
  const [data, setData] = useState<{
    expression: any | null;
    grn: any | null;
    knockdown: any | null;
    insights: any | null;
    aso: any | null;
  }>({
    expression: null,
    grn: null,
    knockdown: null,
    insights: null,
    aso: null,
  });
  const [usingMockData, setUsingMockData] = useState<Record<Tab, boolean>>({
    expression: false,
    grn: false,
    knockdown: false,
    insights: false,
    aso: false,
  });

  // Load data for active tab
  useEffect(() => {
    loadDataForTab(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadDataForTab = async (tab: Tab) => {
    if (data[tab] || isLoading[tab]) return; // Already loaded or loading

    setIsLoading(prev => ({ ...prev, [tab]: true }));
    setErrors(prev => ({ ...prev, [tab]: null }));

    try {
      let result;
      const cellTypes = [...selectedCellTypes.disease, ...selectedCellTypes.normal];

      switch (tab) {
        case 'expression':
          result = await api.getExpressionStats({
            disease: selectedDisease!,
            // Prefer the dataset that generated the analysis results — it's the one that
            // confirmed this gene exists. Fall back to selectedDatasetId (user-selected).
            dataset_id: analysisResult?.dataset_id || selectedDatasetId || undefined,
            lncrnas: [targetGene],
          });
          break;

        case 'grn':
          // Check if target has precomputed GRN
          const targetResult = analysisResult?.lncrna_results?.find(
            (r: any) => r.lncrna === targetGene
          );
          const hasPrecomputedGRN = targetResult?.in_grn || false;

          if (hasPrecomputedGRN) {
            // Try to load precomputed GRN first (faster)
            try {
              result = await api.buildEnhancedGRN({
                gene_list: [targetGene],
                disease: selectedDisease!,
                cell_types: cellTypes,
                dataset_id: selectedDatasetId || undefined,
                correlation_threshold: 0.5,
                include_normal_comparison: true,
              });
            } catch (error: any) {
              // If precomputed GRN fails to load, fall back to correlation-based generation
              console.warn(`Precomputed GRN load failed for ${targetGene}, falling back to correlation-based generation:`, error);
              result = await api.buildCorrelationGRN({
                lncrna: targetGene,
                disease: selectedDisease!,
                cell_types: cellTypes,
                dataset_id: selectedDatasetId || undefined,
                correlation_threshold: 0.8,
                expansion_layers: 2,
              });

              // Transform correlation GRN response to match NetworkGraph expected structure
              if (result && result.disease_grn) {
                result = {
                  ...result,
                  nodes: result.disease_grn.nodes,
                  edges: result.disease_grn.edges,
                };
              }
            }
          } else {
            // No precomputed GRN - generate on-demand using correlation approach
            result = await api.buildCorrelationGRN({
              lncrna: targetGene,
              disease: selectedDisease!,
              cell_types: cellTypes,
              dataset_id: selectedDatasetId || undefined,
              correlation_threshold: 0.8,
              expansion_layers: 2,
            });
          }

          // Transform correlation GRN response to match NetworkGraph expected structure
          if (result && result.disease_grn) {
            // Flatten the structure so NetworkGraph can access nodes/edges directly
            result = {
              ...result,
              nodes: result.disease_grn.nodes,
              edges: result.disease_grn.edges,
            };
          }
          break;

        case 'knockdown':
          result = await api.simulateKnockdown({
            target_gene: targetGene,
            disease: selectedDisease!,
            cell_types: cellTypes,
            dataset_id: selectedDatasetId || undefined,
            include_rescue_score: true,
          });
          break;

        case 'insights':
          result = await api.getTargetInsights({
            target_gene: targetGene,
            disease: selectedDisease!,
            include_literature: true,
            include_pathways: true,
          });
          break;

        case 'aso':
          result = await api.designASO({
            target_gene: targetGene,
            top_n: 10,  // Generate up to 10 ASO sequences
            min_length: 18,
            max_length: 22,
          });
          break;
      }

      setData(prev => ({ ...prev, [tab]: result }));
    } catch (error: any) {
      console.error(`Failed to load ${tab} data:`, error);

      // For GRN 404 errors, show the actual error instead of mock data
      if (tab === 'grn' && error.response?.status === 404) {
        setErrors(prev => ({
          ...prev,
          [tab]: error.response?.data?.detail || `No gene regulatory network found for ${targetGene}. This gene may have insufficient expression or correlations.`,
        }));
      }
      // For GRN 400 errors with structured error response (insufficient correlation)
      else if (tab === 'grn' && error.response?.status === 400) {
        const errorData = error.response?.data?.detail;

        // Check if it's a structured error response
        if (typeof errorData === 'object' && errorData.error_type === 'insufficient_correlation') {
          // Store structured error for rich display
          setErrors(prev => ({
            ...prev,
            [tab]: errorData.error, // Just the error message string
          }));

          // Also store full error data for alternative datasets
          setData(prev => ({
            ...prev,
            [tab]: { error: errorData }
          }));
        } else {
          // Plain string error
          setErrors(prev => ({
            ...prev,
            [tab]: typeof errorData === 'string' ? errorData : error.message,
          }));
        }
      }
      // If endpoint doesn't exist (404) for other tabs, use mock data for development
      else if (error.response?.status === 404) {
        console.log(`Using mock data for ${tab} (endpoint not implemented yet)`);
        const mockData = getMockDataForTab(tab, targetGene);
        setData(prev => ({ ...prev, [tab]: mockData }));
        setUsingMockData(prev => ({ ...prev, [tab]: true }));
      } else {
        setErrors(prev => ({
          ...prev,
          [tab]: error.response?.data?.detail || error.message || `Failed to load ${tab} data`,
        }));
      }
    } finally {
      setIsLoading(prev => ({ ...prev, [tab]: false }));
    }
  };

  // Mock data generator for development
  const getMockDataForTab = (tab: Tab, gene: string) => {
    switch (tab) {
      case 'grn':
        return {
          disease_grn: {
            nodes: [
              { id: gene, label: gene, type: 'lncrna', degree: 15 },
              { id: 'TP53', label: 'TP53', type: 'protein_coding', degree: 12 },
              { id: 'BRCA1', label: 'BRCA1', type: 'protein_coding', degree: 10 },
              { id: 'MYC', label: 'MYC', type: 'protein_coding', degree: 8 },
              { id: 'ESR1', label: 'ESR1', type: 'protein_coding', degree: 7 },
            ],
            edges: [
              { source: gene, target: 'TP53', weight: 0.85 },
              { source: gene, target: 'BRCA1', weight: 0.78 },
              { source: gene, target: 'MYC', weight: 0.72 },
              { source: 'TP53', target: 'MYC', weight: 0.68 },
              { source: 'BRCA1', target: 'ESR1', weight: 0.65 },
            ],
            n_nodes: 5,
            n_edges: 5,
          },
          statistics: {
            node_count: 5,
            edge_count: 5,
            hub_genes: 2,
            communities: 2,
          },
        };

      case 'knockdown':
        return {
          target_gene: gene,
          rescue_score: 68.5,
          effectiveness: 'Moderately Effective',
          baseline_score: 7.8,
          knockdown_score: 2.5,
          affected_pathways: [
            {
              name: 'Cell Cycle Regulation',
              genes_affected: 15,
              impact_score: 72.3,
            },
            {
              name: 'Apoptosis',
              genes_affected: 12,
              impact_score: 65.8,
            },
            {
              name: 'DNA Repair',
              genes_affected: 8,
              impact_score: 58.2,
            },
          ],
          top_rescued_pathways: [
            { pathway: 'Cell Proliferation', rescue_percentage: 75.5 },
            { pathway: 'Migration & Metastasis', rescue_percentage: 68.2 },
            { pathway: 'Angiogenesis', rescue_percentage: 62.8 },
          ],
        };

      case 'insights':
        return {
          summary: `${gene} is a long non-coding RNA that has been implicated in ${selectedDisease}. Research suggests it plays a role in regulating cell proliferation and metastasis through interactions with key oncogenes and tumor suppressors.`,
          literature: [
            {
              title: `Role of ${gene} in cancer progression`,
              abstract: `This study investigates the functional role of ${gene} in cancer cell lines and patient samples. Results show significant upregulation in tumor tissues and association with poor prognosis.`,
              authors: 'Smith J, et al.',
              year: '2023',
              pmid: '37123456',
            },
            {
              title: `${gene} as a therapeutic target`,
              abstract: `Our findings demonstrate that knockdown of ${gene} leads to reduced tumor growth and increased sensitivity to chemotherapy in preclinical models.`,
              authors: 'Johnson M, et al.',
              year: '2024',
              pmid: '38234567',
            },
          ],
          pathways: [
            'Cell Cycle Regulation',
            'Apoptosis',
            'EMT and Metastasis',
            'DNA Damage Response',
            'Immune Evasion',
          ],
        };

      default:
        return null;
    }
  };

  const renderTabContent = () => {
    if (isLoading[activeTab]) {
      // Check if target has precomputed GRN for loading message
      const targetResult = analysisResult?.lncrna_results?.find(
        (r: any) => r.lncrna === targetGene
      );
      const hasPrecomputedGRN = targetResult?.in_grn || false;

      const loadingMessages: Record<Tab, { title: string; subtitle?: string }> = {
        'expression': { title: 'Loading expression statistics...' },
        'grn': {
          title: hasPrecomputedGRN
            ? 'Loading precomputed gene regulatory network...'
            : 'Generating gene regulatory network using correlation analysis...',
          subtitle: hasPrecomputedGRN
            ? 'This should be fast (<1 second)'
            : 'Finding highly correlated genes and expanding with PPI data (may take 5-15 seconds)'
        },
        'knockdown': { title: 'Loading knockdown simulation...' },
        'insights': { title: 'Loading biological insights...' },
        'aso': {
          title: 'Designing antisense oligonucleotides...',
          subtitle: 'Fetching RNA sequence and generating up to 10 ASO candidates'
        }
      };

      const message = loadingMessages[activeTab] || { title: `Loading ${activeTab} data...` };

      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-center max-w-md">
            <Loader2 className="w-12 h-12 text-[var(--origin-blue-500)] animate-spin mx-auto mb-4" />
            <p className="text-[var(--text-secondary)] font-medium mb-2">{message.title}</p>
            {message.subtitle && (
              <p className="text-xs text-[var(--text-tertiary)]">{message.subtitle}</p>
            )}
          </div>
        </div>
      );
    }

    if (errors[activeTab]) {
      // Check if we have structured error data with alternative datasets
      const errorData = data[activeTab]?.error;
      const hasAlternatives = errorData?.error_type === 'insufficient_correlation' &&
                              errorData?.alternative_datasets?.length > 1;

      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-center max-w-2xl px-4">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
              {activeTab === 'grn'
                ? (hasAlternatives ? 'Insufficient Expression Data' : 'Gene Network Unavailable')
                : 'Error Loading Data'}
            </h3>
            <p className="text-[var(--text-secondary)] mb-4">{errors[activeTab]}</p>

            {/* Alternative dataset suggestions for insufficient correlation errors */}
            {hasAlternatives && activeTab === 'grn' && (
              <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-left">
                <div className="flex items-start gap-3 mb-3">
                  <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">
                      Try a Different Dataset
                    </h4>
                    <p className="text-sm text-amber-800 dark:text-amber-200 mb-3">
                      {errorData.suggestion}
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 italic">
                      💡 This lncRNA may have stronger expression patterns in specific cell types or datasets
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* General note for GRN errors */}
            {activeTab === 'grn' && !hasAlternatives && (
              <div className="mt-4 p-4 bg-[var(--bg-secondary)] border border-[var(--border-primary)] rounded-lg text-left text-sm">
                <p className="text-[var(--text-tertiary)] mb-2">
                  <strong className="text-[var(--text-secondary)]">Note:</strong> The GRN badge in the results table indicates that this gene was
                  included in the precomputation process, but the actual network file may not exist if:
                </p>
                <ul className="list-disc list-inside space-y-1 text-[var(--text-tertiary)] pl-2">
                  <li>The gene has very low expression</li>
                  <li>No significant correlations were found</li>
                  <li>Network generation failed during precomputation</li>
                </ul>
                <p className="text-[var(--text-tertiary)] mt-3">
                  Try viewing the <strong className="text-[var(--text-secondary)]">Expression Stats</strong> or <strong className="text-[var(--text-secondary)]">Knockdown Simulation</strong> tabs instead.
                </p>
              </div>
            )}

            <button
              onClick={() => {
                setData(prev => ({ ...prev, [activeTab]: null }));
                setErrors(prev => ({ ...prev, [activeTab]: null }));
                loadDataForTab(activeTab);
              }}
              className="origin-btn origin-btn-secondary mt-4"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'expression':
        return data.expression && data.expression.lncrnas && data.expression.lncrnas.length > 0 ? (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="origin-card p-4">
                <p className="text-xs text-[var(--text-tertiary)] mb-1">Cell Types</p>
                <p className="text-2xl font-bold text-[var(--origin-blue-500)]">
                  {data.expression.n_cell_types}
                </p>
              </div>
              <div className="origin-card p-4">
                <p className="text-xs text-[var(--text-tertiary)] mb-1">Dataset</p>
                <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                  {data.expression.dataset_name || data.expression.dataset_id}
                </p>
              </div>
              <div className="origin-card p-4">
                <p className="text-xs text-[var(--text-tertiary)] mb-1">Query Time</p>
                <p className="text-2xl font-bold text-green-500">
                  {data.expression.query_time_ms?.toFixed(0)}ms
                </p>
              </div>
              <div className="origin-card p-4">
                <p className="text-xs text-[var(--text-tertiary)] mb-1">Status</p>
                <p className="text-sm font-medium text-green-600">
                  Available ⚡
                </p>
              </div>
            </div>

            {/* Per-Cell-Type Expression Statistics */}
            <div className="origin-card p-6">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
                Expression Statistics by Cell Type
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-secondary)]">
                      <th className="text-left p-3 font-semibold text-[var(--text-primary)]">Cell Type</th>
                      <th className="text-right p-3 font-semibold text-[var(--text-primary)]">
                        <span className="inline-flex items-center justify-end gap-1">
                          Log2FC
                          <InfoTooltip title="Log2FC" content="Log2 fold-change: disease vs normal expression. Positive = upregulated in disease, negative = downregulated." position="right" />
                        </span>
                      </th>
                      <th className="text-right p-3 font-semibold text-[var(--text-primary)]">
                        <span className="inline-flex items-center justify-end gap-1">
                          Adj P-value
                          <InfoTooltip title="Adj P-value" content="Benjamini-Hochberg FDR-adjusted p-value from Wilcoxon rank-sum test. < 0.05 = statistically significant." position="right" />
                        </span>
                      </th>
                      <th className="text-right p-3 font-semibold text-[var(--text-primary)]">
                        <span className="inline-flex items-center justify-end gap-1">
                          Disease Mean
                          <InfoTooltip title="Disease Mean" content="Average normalized expression (log1p counts) across disease cells in this cell type." position="right" />
                        </span>
                      </th>
                      <th className="text-right p-3 font-semibold text-[var(--text-primary)]">
                        <span className="inline-flex items-center justify-end gap-1">
                          Normal Mean
                          <InfoTooltip title="Normal Mean" content="Average normalized expression (log1p counts) across normal/healthy cells in this cell type." position="right" />
                        </span>
                      </th>
                      <th className="text-right p-3 font-semibold text-[var(--text-primary)]">
                        <span className="inline-flex items-center justify-end gap-1">
                          Disease Cells
                          <InfoTooltip title="Disease Cells" content="Number of single cells from disease samples analyzed in this cell type." position="right" />
                        </span>
                      </th>
                      <th className="text-right p-3 font-semibold text-[var(--text-primary)]">
                        <span className="inline-flex items-center justify-end gap-1">
                          Normal Cells
                          <InfoTooltip title="Normal Cells" content="Number of single cells from normal/healthy samples analyzed in this cell type." position="right" />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expression.lncrnas[0].cell_type_stats
                      .sort((a: any, b: any) => Math.abs(b.log2fc) - Math.abs(a.log2fc))
                      .map((stat: any, idx: number) => {
                        const isUpregulated = stat.log2fc > 1.0;
                        const isDownregulated = stat.log2fc < -1.0;
                        const isSignificant = stat.adj_pvalue < 0.05;

                        return (
                          <tr
                            key={idx}
                            className={`border-b border-[var(--border-primary)] ${
                              isSignificant
                                ? isUpregulated
                                  ? 'bg-red-900/20'
                                  : isDownregulated
                                  ? 'bg-green-900/20'
                                  : 'hover:bg-[var(--bg-secondary)]'
                                : 'hover:bg-[var(--bg-secondary)]'
                            } transition-colors`}
                          >
                            <td className="p-3 font-medium text-[var(--text-primary)]">
                              {stat.cell_type}
                            </td>
                            <td className="p-3 text-right">
                              <span
                                className={`font-semibold ${
                                  isUpregulated
                                    ? 'text-red-600 dark:text-red-400'
                                    : isDownregulated
                                    ? 'text-green-600 dark:text-green-400'
                                    : 'text-[var(--text-secondary)]'
                                }`}
                              >
                                {stat.log2fc > 0 ? '+' : ''}{stat.log2fc.toFixed(2)}
                              </span>
                            </td>
                            <td className="p-3 text-right text-sm">
                              <span
                                className={
                                  isSignificant
                                    ? 'text-[var(--origin-blue-500)] font-semibold'
                                    : 'text-[var(--text-secondary)]'
                                }
                              >
                                {stat.adj_pvalue < 0.001
                                  ? stat.adj_pvalue.toExponential(2)
                                  : stat.adj_pvalue.toFixed(3)}
                              </span>
                            </td>
                            <td className="p-3 text-right text-[var(--text-secondary)]">
                              {stat.disease_mean.toFixed(2)}
                            </td>
                            <td className="p-3 text-right text-[var(--text-secondary)]">
                              {stat.normal_mean.toFixed(2)}
                            </td>
                            <td className="p-3 text-right text-[var(--text-tertiary)]">
                              {stat.disease_n_cells.toLocaleString()}
                            </td>
                            <td className="p-3 text-right text-[var(--text-tertiary)]">
                              {stat.normal_n_cells.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Legend */}
            <div className="origin-card p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900 dark:text-blue-100">
                  <p className="font-semibold mb-2">Understanding the Statistics</p>
                  <ul className="space-y-1 text-blue-800 dark:text-blue-200">
                    <li><strong>Log2FC:</strong> Log2 fold-change. Positive = upregulated in disease, Negative = downregulated.</li>
                    <li><strong>Adj P-value:</strong> Benjamini-Hochberg adjusted p-value from Wilcoxon rank-sum test. &lt;0.05 = significant.</li>
                    <li className="mt-2 p-2 bg-white dark:bg-gray-800 rounded">
                      <span className="inline-block w-4 h-4 bg-red-200 mr-2"></span>Red highlight = Upregulated (Log2FC &gt; 1.0)
                      <br />
                      <span className="inline-block w-4 h-4 bg-green-200 mr-2"></span>Green highlight = Downregulated (Log2FC &lt; -1.0)
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-20 px-8">
            <AlertCircle className="w-12 h-12 text-[var(--text-tertiary)] mx-auto mb-4" />
            <p className="text-lg font-semibold text-[var(--text-secondary)] mb-3">
              No Statistics Available for {targetGene}
            </p>
            <div className="max-w-2xl mx-auto text-left space-y-3 text-sm text-[var(--text-secondary)] bg-[var(--bg-secondary)] p-4 rounded-lg">
              <p className="font-medium">Why is this happening?</p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>{targetGene} has near-zero or undetectable expression in the selected {selectedDisease} dataset</li>
                <li>It may not have been captured in that dataset's sequencing library</li>
              </ul>
              <p className="font-medium mt-4">What can you do?</p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>View the <strong>Gene Network</strong>, <strong>Knockdown</strong>, or <strong>Insights</strong> tabs — those use precomputed GRN data which has broader coverage</li>
                <li>Try selecting a different dataset for {selectedDisease}</li>
              </ul>
              <p className="text-xs text-[var(--text-tertiary)] mt-4 pt-3 border-t border-[var(--border-primary)]">
                📊 Coverage: precomputed expression stats for 5,000–9,700 lncRNAs per dataset across 163 disease-dataset pairs
              </p>
            </div>
          </div>
        );

      case 'grn':
        return data.grn ? (
          <div className="space-y-6">
            {/* Mock Data Notice */}
            {usingMockData.grn && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-900 font-medium">Preview Mode</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Showing mock data. Backend endpoint <code className="bg-blue-100 px-1 rounded">/api/v1/grn/build-enhanced</code> needs to be implemented.
                  </p>
                </div>
              </div>
            )}
            <div className="origin-card p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-[var(--text-primary)]">
                    Gene Regulatory Network
                  </h3>
                  <p className="text-sm text-[var(--text-secondary)] mt-2">
                    Network showing {targetGene} and its regulatory relationships with other genes.
                    Includes both disease and normal tissue comparisons.
                  </p>
                </div>
                {data.grn.source && (
                  <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${
                    data.grn.source === 'precomputed'
                      ? 'bg-green-900/20 text-green-400 border border-green-800'
                      : 'bg-blue-900/20 text-blue-400 border border-blue-800'
                  }`}>
                    {data.grn.source === 'precomputed' ? (
                      <>
                        <span>⚡</span>
                        <span>Instant</span>
                      </>
                    ) : (
                      <>
                        <span>🔧</span>
                        <span>Built on-demand</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Warnings for relaxed thresholds */}
              {data.grn.warnings && data.grn.warnings.length > 0 && (
                <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">
                        Network Generated with Relaxed Thresholds
                      </p>
                      {data.grn.warnings.map((warning: string, idx: number) => (
                        <p key={idx} className="text-sm text-amber-800 dark:text-amber-200">
                          {warning}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <NetworkGraph
                data={data.grn}
              />
            </div>

            {/* Network Statistics */}
            {data.grn.statistics && (
              <div className="origin-card p-6">
                <h4 className="font-semibold text-[var(--text-primary)] mb-4">
                  Network Statistics
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-[var(--text-tertiary)] mb-1">Nodes</p>
                    <p className="text-2xl font-bold text-[var(--origin-blue-500)]">
                      {data.grn.statistics.node_count}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-tertiary)] mb-1">Edges</p>
                    <p className="text-2xl font-bold text-[var(--origin-blue-500)]">
                      {data.grn.statistics.edge_count}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-tertiary)] mb-1">Hub Genes</p>
                    <p className="text-2xl font-bold text-[var(--origin-blue-500)]">
                      {data.grn.statistics.hub_genes}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-tertiary)] mb-1">Communities</p>
                    <p className="text-2xl font-bold text-[var(--origin-blue-500)]">
                      {data.grn.statistics.communities}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20 px-8">
            <AlertCircle className="w-12 h-12 text-[var(--text-tertiary)] mx-auto mb-4" />
            <p className="text-lg font-semibold text-[var(--text-secondary)] mb-3">
              No GRN Data Available
            </p>
            <p className="text-sm text-[var(--text-tertiary)] max-w-md mx-auto">
              The gene regulatory network could not be loaded for {targetGene}.
              This may occur if the gene is not present in the expression data for this dataset.
            </p>
          </div>
        );

      case 'knockdown':
        return data.knockdown ? (
          <div className="space-y-6">
            {/* Mock Data Notice */}
            {usingMockData.knockdown && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-900 font-medium">Preview Mode</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Showing mock data. Backend endpoint <code className="bg-blue-100 px-1 rounded">/api/v1/knockdown/simulate</code> needs to be implemented.
                  </p>
                </div>
              </div>
            )}
            {/* Rescue Score Visualization */}
            {data.knockdown.rescue_score !== undefined && (
              <RescueScoreBarPlot
                data={{
                  target_gene: targetGene,
                  rescue_score: data.knockdown.rescue_score,
                  effectiveness: data.knockdown.effectiveness || 'Unknown',
                  baseline_score: data.knockdown.baseline_score,
                  knockdown_score: data.knockdown.knockdown_score,
                  top_rescued_pathways: data.knockdown.top_rescued_pathways,
                }}
              />
            )}

            {/* Affected Pathways */}
            {data.knockdown.affected_pathways && data.knockdown.affected_pathways.length > 0 && (
              <div className="origin-card p-6">
                <h4 className="font-semibold text-[var(--text-primary)] mb-4">
                  Affected Pathways
                </h4>
                <div className="space-y-3">
                  {data.knockdown.affected_pathways.map((pathway: any, idx: number) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-[var(--bg-secondary)] rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-[var(--text-primary)]">
                          {pathway.name}
                        </p>
                        <p className="text-sm text-[var(--text-secondary)]">
                          {pathway.genes_affected} genes affected
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-[var(--origin-blue-500)]">
                          {pathway.impact_score.toFixed(1)}%
                        </p>
                        <p className="text-xs text-[var(--text-tertiary)]">Impact</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20 text-[var(--text-secondary)]">
            No knockdown simulation data available
          </div>
        );

      case 'insights':
        return data.insights ? (
          <div className="space-y-6">
            {/* Mock Data Notice */}
            {usingMockData.insights && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-900 font-medium">Preview Mode</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Showing mock data. Backend endpoint <code className="bg-blue-100 px-1 rounded">/api/v1/insights/target</code> needs to be implemented.
                  </p>
                </div>
              </div>
            )}
            {/* Gene Summary */}
            {data.insights.summary && (
              <div className="origin-card p-6">
                <h4 className="font-semibold text-[var(--text-primary)] mb-4">
                  Gene Summary
                </h4>
                <p className="text-[var(--text-secondary)] leading-relaxed">
                  {data.insights.summary}
                </p>
              </div>
            )}

            {/* Literature Evidence */}
            {data.insights.literature && data.insights.literature.length > 0 && (
              <div className="origin-card p-6">
                <h4 className="font-semibold text-[var(--text-primary)] mb-4">
                  Literature Evidence
                </h4>
                <div className="space-y-4">
                  {data.insights.literature.map((article: any, idx: number) => (
                    <div
                      key={idx}
                      className="p-4 bg-[var(--bg-secondary)] rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
                    >
                      <h5 className="font-medium text-[var(--text-primary)] mb-2">
                        {article.title}
                      </h5>
                      <p className="text-sm text-[var(--text-secondary)] mb-2">
                        {article.abstract}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-[var(--text-tertiary)]">
                        <span>{article.authors}</span>
                        <span>•</span>
                        <span>{article.year}</span>
                        {article.pmid && (
                          <>
                            <span>•</span>
                            <a
                              href={`https://pubmed.ncbi.nlm.nih.gov/${article.pmid}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[var(--origin-blue-500)] hover:underline"
                            >
                              PubMed
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Associated Pathways */}
            {data.insights.pathways && data.insights.pathways.length > 0 && (
              <div className="origin-card p-6">
                <h4 className="font-semibold text-[var(--text-primary)] mb-4">
                  Associated Pathways
                </h4>
                <div className="flex flex-wrap gap-2">
                  {data.insights.pathways.map((pathway: string, idx: number) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-[var(--origin-blue-500)]/10 text-[var(--origin-blue-500)] rounded-full text-sm"
                    >
                      {pathway}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20 text-[var(--text-secondary)]">
            No insights data available
          </div>
        );

      case 'aso':
        return data.aso && data.aso.success ? (
          <div className="space-y-6">
            {/* RNA Sequence Information */}
            {data.aso.sequence_info && (
              <div className="origin-card p-6">
                <h4 className="font-semibold text-[var(--text-primary)] mb-4">
                  RNA Sequence Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-[var(--text-tertiary)]">Sequence ID:</span>
                    <span className="ml-2 text-[var(--text-primary)] font-mono">{data.aso.sequence_info.id}</span>
                  </div>
                  <div>
                    <span className="text-[var(--text-tertiary)]">Length:</span>
                    <span className="ml-2 text-[var(--text-primary)]">{data.aso.sequence_info.length} nucleotides</span>
                  </div>
                  {data.aso.sequence_info.description && (
                    <div className="col-span-2">
                      <span className="text-[var(--text-tertiary)]">Description:</span>
                      <span className="ml-2 text-[var(--text-secondary)]">{data.aso.sequence_info.description}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ASO Candidates */}
            {data.aso.top_candidates && data.aso.top_candidates.length > 0 && (
              <div className="origin-card p-6">
                <h4 className="font-semibold text-[var(--text-primary)] mb-4">
                  ASO Candidates (Top {data.aso.top_candidates.length})
                </h4>
                <div className="space-y-4">
                  {data.aso.top_candidates.map((candidate: any, idx: number) => {
                    const qualityScore = (candidate.specificity_score * 100).toFixed(1);
                    const qualityLevel =
                      candidate.specificity_score >= 0.85 ? 'Excellent' :
                      candidate.specificity_score >= 0.75 ? 'Good' : 'Moderate';
                    const qualityColor =
                      candidate.specificity_score >= 0.85 ? 'text-green-400 bg-green-900/20 border-green-800' :
                      candidate.specificity_score >= 0.75 ? 'text-blue-400 bg-blue-900/20 border-blue-800' :
                      'text-orange-400 bg-orange-900/20 border-orange-800';

                    const gcOptimal = candidate.gc_content >= 40 && candidate.gc_content <= 60;
                    const tmOptimal = candidate.tm >= 55 && candidate.tm <= 65;

                    return (
                      <div
                        key={idx}
                        className="p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-primary)]"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className="text-xl font-bold text-[var(--text-primary)]">#{idx + 1}</span>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${qualityColor}`}>
                              {qualityLevel} ({qualityScore}%)
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(candidate.sequence);
                            }}
                            className="px-3 py-1.5 text-xs bg-[var(--origin-blue-500)] text-white rounded-lg hover:bg-[var(--origin-blue-600)] transition-colors"
                          >
                            Copy Sequence
                          </button>
                        </div>

                        <div className="mb-3">
                          <div className="text-xs text-[var(--text-tertiary)] mb-1">ASO Sequence (5' → 3')</div>
                          <div className="font-mono text-sm text-[var(--text-primary)] bg-[var(--bg-primary)] p-3 rounded border border-[var(--border-primary)] break-all">
                            {candidate.sequence}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                          <div>
                            <span className="text-[var(--text-tertiary)]">Position:</span>
                            <span className="ml-2 text-[var(--text-primary)] font-medium">{candidate.position}</span>
                          </div>
                          <div>
                            <span className="text-[var(--text-tertiary)]">Length:</span>
                            <span className="ml-2 text-[var(--text-primary)] font-medium">{candidate.length} nt</span>
                          </div>
                          <div>
                            <span className="text-[var(--text-tertiary)]">GC Content:</span>
                            <span className={`ml-2 font-medium ${gcOptimal ? 'text-green-600' : 'text-orange-600'}`}>
                              {candidate.gc_content.toFixed(1)}%
                            </span>
                          </div>
                          <div>
                            <span className="text-[var(--text-tertiary)]">Tm:</span>
                            <span className={`ml-2 font-medium ${tmOptimal ? 'text-green-600' : 'text-orange-600'}`}>
                              {candidate.tm.toFixed(1)}°C
                            </span>
                          </div>
                          <div>
                            <span className="text-[var(--text-tertiary)]">Accessibility:</span>
                            <span className="ml-2 text-[var(--text-primary)] font-medium">
                              {(candidate.region_accessibility * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Design Criteria */}
            {data.aso.design_criteria && Object.keys(data.aso.design_criteria).length > 0 && (
              <div className="origin-card p-6">
                <h4 className="font-semibold text-[var(--text-primary)] mb-4">
                  Design Criteria
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {Object.entries(data.aso.design_criteria).map(([key, value]: [string, any]) => (
                    <div key={key} className="flex justify-between items-center p-3 bg-[var(--bg-secondary)] rounded">
                      <span className="text-[var(--text-secondary)] capitalize">{key.replace(/_/g, ' ')}:</span>
                      <span className="text-[var(--text-primary)] font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {data.aso.recommendations && data.aso.recommendations.length > 0 && (
              <div className="origin-card p-6">
                <h4 className="font-semibold text-[var(--text-primary)] mb-4">
                  Recommended Next Steps
                </h4>
                <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
                  {data.aso.recommendations.map((rec: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-[var(--origin-blue-500)] mt-1">•</span>
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : data.aso && !data.aso.success ? (
          <div className="text-center py-20">
            <AlertCircle className="w-12 h-12 text-[var(--error)] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
              ASO Design Failed
            </h3>
            <p className="text-[var(--text-secondary)]">
              {data.aso.error || 'Unable to design ASO sequences for this target'}
            </p>
          </div>
        ) : (
          <div className="text-center py-20 text-[var(--text-secondary)]">
            No ASO data available
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4">
      <div className="bg-[var(--bg-primary)] rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--border-primary)] flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[var(--text-primary)]">
              {targetGene}
            </h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              Target Analysis for {selectedDisease}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[var(--bg-secondary)] rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-[var(--text-secondary)]" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-[var(--border-primary)]">
          <div className="flex items-center gap-1 -mb-px">
            {[
              { id: 'expression' as Tab, label: 'Expression Stats', icon: BarChart3 },
              { id: 'grn' as Tab, label: 'Gene Network', icon: Network },
              { id: 'knockdown' as Tab, label: 'Knockdown Simulation', icon: Activity },
              { id: 'insights' as Tab, label: 'Biological Insights', icon: BookOpen },
              { id: 'aso' as Tab, label: 'ASO Design', icon: Dna },
            ].map(tab => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 font-medium text-sm transition-colors border-b-2 ${
                    isActive
                      ? 'border-[var(--origin-blue-500)] text-[var(--origin-blue-500)]'
                      : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default TargetDetailsModal;
